/**
 * @module
 * a simple factory for creating an event emitter similar
 * to nodes eventEmitter.
 */
const eventEmitter = function() {
    const listeners = {};

    /**
     * adds an event listener to the event with the provided name
     * @param {String} name the name of the event
     * @param {Function} fn the function to run when event is triggered
     * @return {Object} returns the event emitter.
     */
    const on = function(name, fn) {
        const names = Array.isArray(name) ? name : [name];

        if (typeof fn !== 'function') {
            throw new Error('you must supply a callback');
        }

        names.forEach((name) => {
            if (listeners.hasOwnProperty(name)) {
                listeners[name].push(fn);
                return;
            }
            listeners[name] = [fn];
        });

        return this;
    };

    /**
     * removs an or all event listeners
     * @param {String} name the name of the event
     * @param {Function} fn the callback
     * @return {Object} returns the event emitter.
     */
    const off = function(name, fn) {
        const names = Array.isArray(name) ? name : [name];
        names.forEach((name) => {
            if (!listeners.hasOwnProperty(name)) { return; }
            if (!fn) { delete listeners[name]; return; }
            var index = listeners[name].indexOf(fn);
            if (index === -1) { return; }
            listeners[name].splice(index, 1);
        });
        return this;
    };

    /**
     * triggers all the listeners listening to the event name
     * and supply the data.
     * @param {String} name the name of the event
     * @param {Object} data the data to supply to the callbacks
     * @return {Object} the eventemitter.
     */
    const trigger = function(name, ...eventData) {
        if (listeners.hasOwnProperty(name)) {
            // we clone the list of listeners and loop that - because a common pattern is to trigger an 
            // event and then remove the eventlistener - all of which happens in one cycle of the *for* loop . 
            // This splices the array of listening functions that we are currently looping through - causing 
            // nasty nasty problems.
            // Cloning the list of listening functions protects the event loop.
            const listenersNameClone = listeners[name].slice(0);
            const listenersNameCloneLength = listenersNameClone.length;
            for(let i = 0; i < listenersNameCloneLength; i++) {
                listenersNameClone[i].apply(this, eventData);
            }
        }
        return this;
    };

    /**
     * checks whether any listeners have been registered
     * on the event with the provided name.
     * @param {String} name the name of the event
     * @return {Boolean} whether any listeners are registered
     */
    const hasListeners = function(name) {
        return listeners.hasOwnProperty(name);
    };

    return { on, off, trigger, hasListeners };
};

export default eventEmitter;
